// Voice-note recorder for the notes composer (mock s26: "🎤 הקלטה קולית") —
// the MediaRecorder capture logic is a PORT of src/components/chat/
// VoiceRecorder.tsx (mime picking, min/max duration, cancel-vs-stop refs,
// unmount cleanup), per the CRM revamp rule: reuse the logic, never the legacy
// component. Differences from the chat recorder: the audio is KEPT — uploaded
// to Firebase Storage (MediaUpload.tsx's client-upload flow, uid-keyed path) —
// and the transcription becomes the note text (best-effort: a failed
// transcription still saves the voice note with empty text, per the UserNote
// schema contract). The live waveform visualization was intentionally not
// ported (visual nicety only; keeps this dependency-free of WebAudio).
//
// ALSO REUSED BY the משימות tab's quick-add (tabs/tasks/TaskQuickAdd.tsx), so
// the customer gets one identical recording experience in both tabs. That reuse
// is expressed purely through the optional props below (uploadAudio / label /
// savingLabel / saveErrorMessage / disabledReason) — each defaults to the
// original notes behaviour, so the notes composer is unaffected.
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { Close, Mic, Stop } from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import { transcribeAudio } from "services/chat";
import { uploadVoiceNoteBlob } from "./notesData";

// Voice notes are a spoken thought, not a monologue: 2 minutes keeps the
// base64 transcription payload far below the 5MB request body limit even on
// high-bitrate codecs (opus@~32kbps → ~480KB for 120s).
const MAX_DURATION_MS = 120_000;
// Anything shorter is an accidental tap — silently discarded.
const MIN_DURATION_MS = 500;

type Status = "idle" | "requesting" | "recording" | "saving";

type VoiceNoteRecorderProps = {
  contactId: string;
  /** Persists the note (audioUrl + transcription). Rejection = save failure. */
  onSave: (args: { audioUrl: string; transcription: string }) => Promise<void>;
  disabled?: boolean;
  // --- Optional reuse hooks (added for the tasks tab; every default below
  // reproduces the original notes behaviour exactly, so existing callers that
  // pass none of them are unaffected). ---
  /**
   * Upload the recording to Firebase Storage and hand back its URL (default
   * true — the notes behaviour). Pass false when the consumer has nowhere to
   * store audio (a CalendarEvent task has no audio field), which skips the
   * upload round-trip entirely and calls onSave with an empty audioUrl.
   */
  uploadAudio?: boolean;
  /** Idle button label. */
  label?: string;
  /** Caption shown while uploading/transcribing/saving. */
  savingLabel?: string;
  /** Snackbar message when the upload/transcribe/onSave step throws. */
  saveErrorMessage?: string;
  /**
   * Tooltip explaining WHY the control is greyed out, shown only while the
   * recorder is idle AND `disabled` (a disabled control has no other way to
   * explain itself). Omit for the historical behaviour: a plain disabled
   * button with no tooltip.
   */
  disabledReason?: string;
};

// First MediaRecorder MIME the browser supports; empty string lets the browser
// pick. opus-in-webm/ogg transcribe cleanly; mp4/AAC is the Safari fallback.
const pickMimeType = (): string => {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/ogg",
    "audio/mp4"
  ];
  for (const candidate of candidates) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate;
  }
  return "";
};

const formatDuration = (ms: number): string => {
  const totalSec = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// Blob → base64 without FileReader, chunked to avoid the call-stack limit of
// String.fromCharCode(...hugeArray) (ported from the chat recorder).
const blobToBase64 = async (blob: Blob): Promise<string> => {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, i + CHUNK) as unknown as number[]
    );
  }
  return btoa(binary);
};

export function VoiceNoteRecorder({
  contactId,
  onSave,
  disabled,
  uploadAudio = true,
  label = "הקלטה קולית",
  savingLabel = "שומר הערה קולית…",
  saveErrorMessage = "שמירת ההערה הקולית נכשלה",
  disabledReason
}: VoiceNoteRecorderProps): JSX.Element {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [status, setStatus] = useState<Status>("idle");
  const [durationMs, setDurationMs] = useState(0);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const tickerRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  // Distinguishes "cancel" from "stop": both end the recorder, only stop saves.
  const cancelledRef = useRef(false);
  const mimeTypeRef = useRef("");
  // Lets the auto-stop interval call the latest finish() without stale closures.
  const finishRef = useRef<() => void>(() => {});
  const mountedRef = useRef(true);

  const cleanup = useCallback(() => {
    if (tickerRef.current != null) {
      window.clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  // Free the mic if the page unmounts mid-recording (tabs stay mounted on tab
  // switches — this runs only when the customer page itself closes).
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        if (recorderRef.current?.state === "recording") {
          cancelledRef.current = true;
          recorderRef.current.stop();
        }
      } catch {
        // recorder already in a terminal state
      }
      cleanup();
    };
  }, [cleanup]);

  const safeSetStatus = useCallback((next: Status) => {
    if (mountedRef.current) setStatus(next);
  }, []);

  const start = useCallback(async () => {
    if (status !== "idle" || disabled) return;
    setStatus("requesting");
    cancelledRef.current = false;
    chunksRef.current = [];
    setDurationMs(0);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      // The page may have unmounted while the permission prompt was up — the
      // unmount cleanup already ran and found nothing to stop, so continuing
      // would start a headless recording that keeps the mic hot forever.
      if (!mountedRef.current || cancelledRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        safeSetStatus("idle");
        return;
      }
      streamRef.current = stream;

      const mimeType = pickMimeType();
      mimeTypeRef.current = mimeType;
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = async () => {
        const elapsed = Date.now() - startedAtRef.current;
        cleanup();

        if (cancelledRef.current || elapsed < MIN_DURATION_MS) {
          safeSetStatus("idle");
          return;
        }

        const blobMime = mimeTypeRef.current.split(";")[0] || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobMime });
        if (blob.size === 0) {
          safeSetStatus("idle");
          return;
        }

        safeSetStatus("saving");
        try {
          // 1) Keep the audio — upload first so a transcription hiccup can
          //    never lose the recording. Consumers with nowhere to store audio
          //    (uploadAudio={false}) skip this and get an empty audioUrl.
          const audioUrl = uploadAudio ? await uploadVoiceNoteBlob(blob, blobMime, contactId) : "";
          // 2) Best-effort transcription → the note text (empty on failure,
          //    per the UserNote.audioUrl schema contract).
          let transcription = "";
          try {
            transcription = await transcribeAudio({
              audioBase64: await blobToBase64(blob),
              mimeType: blobMime
            });
          } catch (transcribeError) {
            console.error("VoiceNoteRecorder: transcription failed", transcribeError);
          }
          await onSave({ audioUrl, transcription: transcription.trim() });
        } catch (error) {
          console.error("VoiceNoteRecorder: saving voice note failed", error);
          enqueueSnackbar(saveErrorMessage, {
            variant: "error",
            autoHideDuration: 4000
          });
        } finally {
          safeSetStatus("idle");
        }
      };

      startedAtRef.current = Date.now();
      tickerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startedAtRef.current;
        if (mountedRef.current) setDurationMs(elapsed);
        if (elapsed >= MAX_DURATION_MS) finishRef.current();
      }, 200);

      // 250ms timeslice keeps memory low and guarantees data on a quick stop.
      recorder.start(250);
      safeSetStatus("recording");
    } catch (error: any) {
      const message =
        error?.name === "NotAllowedError" || error?.name === "SecurityError"
          ? "אין הרשאת מיקרופון בדפדפן — יש לאשר גישה כדי להקליט"
          : "הפעלת ההקלטה נכשלה";
      enqueueSnackbar(message, { variant: "error", autoHideDuration: 4000 });
      cleanup();
      safeSetStatus("idle");
    }
  }, [
    status,
    disabled,
    contactId,
    onSave,
    cleanup,
    safeSetStatus,
    enqueueSnackbar,
    uploadAudio,
    saveErrorMessage
  ]);

  const finish = useCallback(() => {
    if (status !== "recording") return;
    cancelledRef.current = false;
    try {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    } catch {
      // already stopped
    }
  }, [status]);

  const cancel = useCallback(() => {
    if (status !== "recording") return;
    cancelledRef.current = true;
    try {
      if (recorderRef.current?.state === "recording") recorderRef.current.stop();
    } catch {
      // already stopped
    }
  }, [status]);

  useEffect(() => {
    finishRef.current = finish;
  }, [finish]);

  if (status === "saving") {
    return (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, px: 1, height: 30 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
          {savingLabel}
        </Typography>
      </Box>
    );
  }

  if (status === "recording" || status === "requesting") {
    return (
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          height: 30,
          px: 0.25,
          borderRadius: 15,
          bgcolor: alpha(theme.palette.error.main, 0.08),
          border: `1px solid ${alpha(theme.palette.error.main, 0.24)}`
        }}
      >
        <Tooltip title="ביטול ההקלטה">
          <span>
            <IconButton size="small" onClick={cancel} aria-label="ביטול ההקלטה">
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            fontVariantNumeric: "tabular-nums",
            color: "text.secondary",
            fontWeight: 600,
            minWidth: 32,
            textAlign: "center"
          }}
        >
          {formatDuration(durationMs)}
        </Typography>
        <Tooltip title="סיום ושמירה">
          <span>
            <IconButton
              size="small"
              onClick={finish}
              aria-label="סיום ההקלטה ושמירה"
              sx={{
                bgcolor: theme.palette.error.main,
                color: theme.palette.error.contrastText,
                "&:hover": { bgcolor: theme.palette.error.dark },
                animation: status === "recording" ? "wbVnPulse 1.4s ease-in-out infinite" : "none",
                "@keyframes wbVnPulse": {
                  "0%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0.5)}` },
                  "70%": { boxShadow: `0 0 0 7px ${alpha(theme.palette.error.main, 0)}` },
                  "100%": { boxShadow: `0 0 0 0 ${alpha(theme.palette.error.main, 0)}` }
                }
              }}
            >
              <Stop sx={{ fontSize: 16 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  // Idle — and the ONLY branch that reads `disabled`. The saving/recording
  // branches above return first and never consult it, so a consumer flipping
  // `disabled` to true mid-recording (e.g. the customer starts typing while the
  // mic is hot) can never yank the stop/cancel controls out from under them;
  // the recording simply finishes and the button comes back greyed. `start()`
  // is likewise guarded by `status !== "idle"`.
  const idleButton = (
    <Button
      size="small"
      color="inherit"
      onClick={() => void start()}
      disabled={disabled}
      startIcon={<Mic sx={{ fontSize: 16 }} />}
      sx={{
        // Explicit grey while disabled: the sx `color` would otherwise fight
        // MUI's `.Mui-disabled` rule and leave the label looking clickable.
        color: disabled ? "text.disabled" : "text.secondary",
        whiteSpace: "nowrap",
        flexShrink: 0
      }}
    >
      {label}
    </Button>
  );

  if (!disabled || !disabledReason) return idleButton;

  // A disabled MUI control swallows pointer events, so a Tooltip attached
  // straight to it never fires — the span gives the tooltip a live hover target
  // (same wrapper pattern as the cancel/stop buttons above).
  return (
    <Tooltip title={disabledReason}>
      <span style={{ display: "inline-flex", flexShrink: 0 }}>{idleButton}</span>
    </Tooltip>
  );
}
